import React from 'react';
import { 
  ExclamationCircleIcon, 
  InformationCircleIcon,
  CheckCircleIcon 
} from '@heroicons/react/24/outline';

interface ValidationHelperProps {
  isVisible: boolean;
  validationErrors: string[];
  onClose: () => void;
}

const ValidationHelper: React.FC<ValidationHelperProps> = ({ 
  isVisible, 
  validationErrors, 
  onClose 
}) => {
  if (!isVisible) return null;

  const hasErrors = validationErrors.length > 0;

  return (
    <div className="absolute top-4 right-4 z-50 max-w-md">
      <div className={`
        bg-white rounded-lg shadow-xl border-2 
        ${hasErrors ? 'border-red-400' : 'border-green-400'}
        overflow-hidden
      `}>
        {/* Header */}
        <div className={`
          px-4 py-3 flex items-center justify-between
          ${hasErrors ? 'bg-red-50' : 'bg-green-50'}
        `}>
          <div className="flex items-center">
            {hasErrors ? (
              <>
                <ExclamationCircleIcon className="h-5 w-5 text-red-600 mr-2" />
                <h3 className="text-sm font-semibold text-red-900">
                  Workflow Issues ({validationErrors.length})
                </h3>
              </>
            ) : (
              <>
                <CheckCircleIcon className="h-5 w-5 text-green-600 mr-2" />
                <h3 className="text-sm font-semibold text-green-900">
                  Workflow Valid
                </h3>
              </>
            )}
          </div>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600"
          >
            <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {/* Content */}
        <div className="px-4 py-3 max-h-96 overflow-y-auto">
          {hasErrors ? (
            <div className="space-y-2">
              <p className="text-xs text-gray-600 mb-3">
                Fix these issues to create a valid workflow:
              </p>
              {validationErrors.map((error, index) => {
                // Parse the icon from the error message
                const iconMatch = error.match(/^(🚫|⚠️|🔗|❌)/);
                const icon = iconMatch ? iconMatch[1] : '•';
                const message = iconMatch ? error.substring(iconMatch[0].length).trim() : error;
                
                return (
                  <div 
                    key={index} 
                    className="flex items-start text-xs bg-gray-50 rounded p-2 hover:bg-gray-100 transition-colors"
                  >
                    <span className="mr-2 flex-shrink-0 text-base">{icon}</span>
                    <span className="text-gray-700">{message}</span>
                  </div>
                );
              })}
            </div>
          ) : (
            <div className="text-center py-4">
              <CheckCircleIcon className="h-12 w-12 text-green-500 mx-auto mb-2" />
              <p className="text-sm text-gray-600">
                Your workflow is properly configured!
              </p>
              <p className="text-xs text-gray-500 mt-1">
                You can save or publish this workflow.
              </p>
            </div>
          )}
        </div>

        {/* Footer with tips */}
        {hasErrors && (
          <div className="px-4 py-3 bg-gray-50 border-t border-gray-200">
            <div className="flex items-start">
              <InformationCircleIcon className="h-4 w-4 text-blue-500 mr-2 flex-shrink-0 mt-0.5" />
              <div className="text-xs text-gray-600">
                <p className="font-semibold mb-1">Quick Tips:</p>
                <ul className="space-y-0.5">
                  <li>• Drag from node handles to create connections</li>
                  <li>• Double-click connections to add labels</li>
                  <li>• Press Delete to remove selected items</li>
                </ul>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default ValidationHelper;